import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ArrowsClockwise, Lightning } from "@phosphor-icons/react";
import { refineOffer } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const SUGGESTIONS = [
  "Make the bonuses more outcome-specific.",
  "Make the language bolder and more direct.",
  "Push more towards pay-for-performance pricing.",
  "Shorten the time delay and emphasize speed of results.",
  "Add a risk-reversal stronger than money-back.",
];

export default function RefineDialog({ open, onOpenChange, offerId }) {
  const [instructions, setInstructions] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { refresh, appConfig } = useAuth();
  const showPricing = appConfig?.show_pricing ?? false;

  const submit = async () => {
    if (!instructions.trim()) return;
    setLoading(true);
    try {
      const newOffer = await refineOffer(offerId, instructions.trim());
      toast.success("Refined offer forged");
      await refresh();
      onOpenChange(false);
      navigate(`/offer/${newOffer.id}`);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 402) {
        toast.error("Out of credits");
        onOpenChange(false);
        if (showPricing) navigate("/pricing");
        return;
      }
      toast.error(err?.response?.data?.detail || "Refinement failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0F1F38] border-white/10 text-[#F5F1E8] max-w-lg"
        data-testid="refine-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-bold tracking-tight flex items-center gap-2">
            <ArrowsClockwise weight="bold" className="h-5 w-5 text-[#C9A961]" />
            Refine this offer
          </DialogTitle>
          <DialogDescription className="text-[#9AA5B5]">
            Tell us what to change. We&apos;ll re-forge a new version (uses 1
            credit).
          </DialogDescription>
        </DialogHeader>

        <Textarea
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          placeholder="e.g. Push harder on pay-for-performance, make bonuses more specific to e-commerce, shorten the timeline."
          rows={5}
          className="bg-[#0A1628] border-white/15 focus-visible:ring-[#C9A961] focus-visible:border-[#C9A961] text-[#F5F1E8] placeholder:text-white/40 resize-none rounded-sm mt-2"
          data-testid="refine-dialog-textarea"
        />

        <div className="flex flex-wrap gap-2 mt-1">
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setInstructions((curr) => (curr ? curr + " " + s : s))}
              className="text-xs px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 text-[#9AA5B5] hover:text-[#F5F1E8] border border-white/5 transition-colors"
              data-testid={`refine-suggestion-${s.slice(0, 12).replace(/\s+/g, "-")}`}
            >
              {s}
            </button>
          ))}
        </div>

        <div className="flex items-center justify-end gap-3 mt-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            className="text-[#9AA5B5] hover:text-white hover:bg-white/5"
            data-testid="refine-dialog-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!instructions.trim() || loading}
            className="bg-[#C9A961] text-[#0A1628] font-bold hover:bg-[#E0B64B] rounded-sm px-6 disabled:opacity-40"
            data-testid="refine-dialog-submit-btn"
          >
            {loading ? (
              <>
                <div className="mr-2 h-4 w-4 rounded-full border-2 border-black border-t-transparent animate-spin" />
                Refining…
              </>
            ) : (
              <>
                <Lightning weight="fill" className="mr-2 h-4 w-4" />
                Re-forge offer
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
